import { useEffect, useRef } from "react";

import { useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";

interface UseScrollToHashProps {
  hash: string;
  isEnabled?: boolean;
}

/**
 * Scrolls the returned ref's element into view when the URL hash equals `hash`.
 * Pass `isEnabled` as false while the section is still loading: animating toward
 * a target that is still shifting glitches. `location.key` also re-runs it when
 * the user navigates to the same URL again, e.g. clicking the same chat while
 * already on its page.
 */
export function useScrollToHash(props: UseScrollToHashProps) {
  const { hash, isEnabled = true } = props;
  const sectionRef = useRef<HTMLDivElement>(null);
  const { hash: currentHash, key } = useLocation();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  useEffect(() => {
    if (!isEnabled || currentHash !== hash) return undefined;
    const frameId = requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentHash, hash, isEnabled, key, prefersReducedMotion]);

  return sectionRef;
}
